import styles from './styles.module.scss';

import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { ControlWrapper } from '../../../../components';
import { useBookingAssistance } from '../../hooks';

interface ContentProps {
  id: number;
}

export const BookingAssistance = () => {
  const { watch } = useFormContext();
  const id = watch('id');
  const payments = watch('payments');

  if (id && payments?.length === 0) {
    return <BookingAssistanceContent id={id} />;
  }

  return null;
};

export const BookingAssistanceContent = ({ id }: ContentProps) => {
  const { t } = useTranslation();

  const { watch } = useFormContext();

  const discountDate = watch('discountDate');
  const payments = watch('payments');
  const remaining = watch('remainingAmount');
  const discount = watch('discountPercent');
  const isInDatev = payments.some((x) => x.datevDate);

  const { data, canCreate, create, edit } = useBookingAssistance({
    invoiceId: id,
    discount,
    remaining,
  });

  const [registerSkonto, setRegsiterSkonto] = useState(true);
  const [manualPaymentEdit, setManualPaymentEdit] = useState(false);

  const handleCreate = useCallback(() => {
    create(registerSkonto);
  }, [create, registerSkonto]);

  const handleRegisterSkontoChange = useCallback(
    (_, value: boolean) => {
      setRegsiterSkonto(value);

      if (!value && !manualPaymentEdit) {
        edit('paymentAmount', data.savedPaymentAmount);
      }
    },
    [data.savedPaymentAmount, edit, manualPaymentEdit]
  );

  return (
    <div className={styles.bookingAssistance}>
      <Divider>{t('PAYMENT.BOOKING_ASSISTANCE_TITLE')}</Divider>

      <Grid container spacing="1rem" padding="1rem 0">
        <Grid item xs={6}>
          <Stack gap="0.25rem">
            <NumberInput
              label={t('COMMON.RA.DISCOUNT')}
              unit={'%'}
              value={registerSkonto ? data.discount : 0}
              onChange={(value) => {
                edit('discount', value);
              }}
              min={0}
              max={100}
              disabled={!registerSkonto || payments?.length}
              startAdornment={
                <Box mb="-1rem">
                  <CheckboxRadioItem
                    disabled={false}
                    label=""
                    checked={registerSkonto}
                    onChange={handleRegisterSkontoChange}
                  />
                </Box>
              }
            />
            {discount ? (
              <Typography variant="caption">
                {t('PAYMENT.SKONTO_UNTIL', {
                  discount,
                  date: DateTime.fromISO(discountDate).toFormat('dd.MM.yyyy'),
                })}
              </Typography>
            ) : null}
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <NumberInput
            label={t('COMMON.DISCOUNT_AMOUNT')}
            unit={'€'}
            type="number"
            value={registerSkonto ? data.discountAmount : 0}
            onChange={(value) => {
              edit('discountAmount', value);
            }}
            decimals={2}
            disabled={!registerSkonto || payments?.length}
          />
        </Grid>
      </Grid>

      <ControlWrapper columns={2}>
        <NumberInput
          label={t('INPUTS.PAYMENT')}
          unit={'€'}
          type="number"
          value={data.paymentAmount}
          onChange={(value) => {
            setManualPaymentEdit(true);
            edit('paymentAmount', value);
          }}
          decimals={2}
          disabled={payments?.length}
        />
        <Button
          disabled={!canCreate || isInDatev || payments?.length}
          startIcon={<CheckIcon />}
          onClick={handleCreate}
        >
          {t('PAYMENT.BOOKING_ASSISTANCE_BUTTON')}
        </Button>
      </ControlWrapper>
    </div>
  );
};
